import React, { useState } from "react";
import { RunnerGame } from "../../components/RunnerGame";
import { useCursor } from "../../providers/CursorContext";
import { useTheme } from "../../providers/ThemeContext";

import instagramIcon from "../../assets/images/instagram.webp";
import linkedinIcon from "../../assets/images/linkedin.webp";
import dribbbleIcon from "../../assets/images/dribbble.webp";
import githubIcon from "../../assets/images/github.webp";

interface SocialMediaItem {
  name: string;
  handle: string;
  icon: string;
  url: string;
}

const SOCIAL_ITEMS: SocialMediaItem[] = [
  {
    name: "Instagram",
    handle: "@yasfdany",
    icon: instagramIcon,
    url: "https://instagram.com/yasfdany",
  },
  {
    name: "LinkedIn",
    handle: "yasfdany",
    icon: linkedinIcon,
    url: "https://www.linkedin.com/in/yasfdany/",
  },
  {
    name: "Dribbble",
    handle: "yasfdany",
    icon: dribbbleIcon,
    url: "https://dribbble.com/yasfdany",
  },
  {
    name: "GitHub",
    handle: "yasfdany",
    icon: githubIcon,
    url: "https://github.com/yasfdany",
  },
];

export const CreditSection: React.FC = () => {
  const { setCursorSize, setHover } = useCursor();
  const { theme } = useTheme();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const handleIconClick = (url: string) => {
    window.open(url, "_blank");
  };

  return (
    <div className="w-full h-full flex flex-col justify-between z-10 relative">
      {/* Top Section - Let's Collaborate positioned absolute to top-center */}
      <div className="absolute left-1/2 -translate-x-1/2 top-24 md:top-[12vw] flex flex-col items-center text-center z-20 animate-[slideUp_0.8s_ease-out] w-full max-w-[90vw]">
        <h2 className="font-nunito text-[40px] md:text-[86px] font-black text-dark leading-[1.05] mb-6 md:mb-[2.5vw] select-text whitespace-pre-line text-center">
          Let's{"\n"}Collaborate
        </h2>

        {/* Desktop View: Social Icons Row */}
        <div className="hidden md:flex items-center justify-center gap-[12px]">
          {SOCIAL_ITEMS.map((item, idx) => {
            const isItemHovered = hoveredIndex === idx;

            return (
              <div
                key={idx}
                onClick={() => handleIconClick(item.url)}
                onMouseEnter={() => {
                  setHoveredIndex(idx);
                  setHover(true);
                  setCursorSize(48);
                }}
                onMouseLeave={() => {
                  setHoveredIndex(null);
                  setHover(false);
                  setCursorSize(24);
                }}
                className="cursor-pointer transition-all duration-200 select-none flex items-center justify-center p-4 active:scale-95"
                style={{
                  transform: isItemHovered
                    ? "scale(1.2) translateY(-4px) rotate(2deg)"
                    : "scale(1) translateY(0) rotate(0)",
                }}
              >
                <img
                  src={item.icon}
                  alt={item.name}
                  className="w-8 h-8 md:w-[42px] md:h-[42px] object-contain transition-colors duration-200"
                  style={{
                    filter: isItemHovered
                      ? idx === 0
                        ? "invert(24%) sepia(85%) saturate(2311%) hue-rotate(224deg) brightness(91%) contrast(93%)" // instagram #515bd4
                        : idx === 1
                        ? "invert(36%) sepia(87%) saturate(541%) hue-rotate(164deg) brightness(92%) contrast(89%)" // linkedin #2678b7
                        : idx === 2
                        ? "invert(46%) sepia(50%) saturate(1637%) hue-rotate(309deg) brightness(99%) contrast(92%)" // dribbble #ea4c89
                        : "invert(64%) sepia(21%) saturate(2421%) hue-rotate(85deg) brightness(94%) contrast(83%)" // github #37ba4e
                      : theme === "dark"
                      ? "brightness(0) invert(0.9)"
                      : "invert(13%) sepia(0%) saturate(614%) hue-rotate(166deg) brightness(94%) contrast(87%)", // Themes.black #2A2A2A
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* Mobile View: Social Cards List */}
        <div className="flex md:hidden flex-col gap-4 mt-4 w-64 z-30">
          {SOCIAL_ITEMS.map((item, idx) => {
            const isItemHovered = hoveredIndex === idx;

            return (
              <div
                key={idx}
                onClick={() => handleIconClick(item.url)}
                onMouseEnter={() => {
                  setHoveredIndex(idx);
                  setHover(true);
                  setCursorSize(48);
                }}
                onMouseLeave={() => {
                  setHoveredIndex(null);
                  setHover(false);
                  setCursorSize(24);
                }}
                className="cursor-pointer transition-all duration-200 select-none flex items-center gap-3.5 py-3 px-5 rounded-[14px] bg-bg border border-stroke shadow-sm hover:bg-neutral-100 dark:hover:bg-neutral-900 active:scale-[0.98] w-full"
                style={{
                  transform: isItemHovered
                    ? "translateX(4px)"
                    : "translateX(0)",
                }}
              >
                <img
                  src={item.icon}
                  alt={item.name}
                  className="w-5 h-5 object-contain transition-colors duration-200"
                  style={{
                    filter: isItemHovered
                      ? idx === 0
                        ? "invert(24%) sepia(85%) saturate(2311%) hue-rotate(224deg) brightness(91%) contrast(93%)" // instagram #515bd4
                        : idx === 1
                        ? "invert(36%) sepia(87%) saturate(541%) hue-rotate(164deg) brightness(92%) contrast(89%)" // linkedin #2678b7
                        : idx === 2
                        ? "invert(46%) sepia(50%) saturate(1637%) hue-rotate(309deg) brightness(99%) contrast(92%)" // dribbble #ea4c89
                        : "invert(64%) sepia(21%) saturate(2421%) hue-rotate(85deg) brightness(94%) contrast(83%)" // github #37ba4e
                      : theme === "dark"
                      ? "brightness(0) invert(0.9)"
                      : "invert(13%) sepia(0%) saturate(614%) hue-rotate(166deg) brightness(94%) contrast(87%)", // Themes.black #2A2A2A
                  }}
                />
                <div className="flex flex-col text-left leading-tight font-nunito">
                  <span className="font-bold text-sm text-dark">
                    {item.name}
                  </span>
                  <span className="text-[10px] text-dark/60 dark:text-white/60">
                    {item.handle}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom Section - Runner Game Canvas */}
      <div className="w-full mt-auto">
        <RunnerGame />
      </div>
    </div>
  );
};
